import { PrimaryButton } from "@components/Buttons";
import CalendarIcon from "@components/Icons/CalendarIcon";
import CheckedIcon from "@components/Icons/CheckedIcon";
import FossIdVerificationRequest from "@components/Project/FossIdVerificationRequest";
import ClarityAirVerificationRequest from "@components/Project/ClarityAirVerificationRequest";
import FullScreenDotLoader from "@components/UI/FullScreenDotLoader";
import Spin from "@components/UI/Spin";
import { PROGRESS_FOSSID_STATUS, SCAN_SITE_LINK } from "@constants/index";
import { PermissionContext } from "@contexts/PermissionContext";
import { useClientConfig } from "@contexts/ClientConfigContext";
import { useFossIdVerificationRequest } from "@hooks/useFossIdVerificationRequest";
import { useClarityAirVerificationRequest } from "@hooks/useClarityAirVerificationRequest";
import { useTheme } from "@hooks/useTheme";
import { useUploadFileWithGit } from "@hooks/useUploadFileWithGit";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ConfirmProcessingModal, ConfirmActionModal } from "./index";
import DotLoader from "react-spinners/DotLoader";
import { createWorkflowTestId, workflowStepSlug } from "@utils/workflowTestId";
import { CustomDatePicker } from "@components/Forms";

const WorkflowProgressFossID = ({
  steps = [],
  currentStep = 0,
  className = "",
  projectManagerId,
  onRejectVerification,
  onDecompleteFromDone,
  onMoveToUsageTypeCheck,
  onMoveToLoadIdentifications,
  onMoveToUsageTypeInput,
  onMoveToReview,
  onMoveToFollowUp,
  onMoveToFollowCheckUp,
  onRequestReVerification,
  onMoveToDone,
  onCompleteVerification,
  onToFollowUpResult,
  onRejectVerificationFromOnVerification,
  onRejectUsageTypeCheck,
  onRejectUsageTypeInput,
  onRejectUsageTypeReview,
  // New props for re-upload functionality
  currentVerificationData = null,
  forceCompleteText = false,
  isLoading = false,
  isFetching = false,
  verificationType = "fossid",
  dataTestId,
}) => {
  const { colors } = useTheme();
  const tid = createWorkflowTestId(dataTestId ?? `workflow-progress-${verificationType}`);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { projectSeq } = useParams();

  const isClarityAirTool = verificationType === "clarityair";
  const activeTabForNav = isClarityAirTool ? "clarityair" : "fossid";
  const scannerSiteName = isClarityAirTool ? "Clarity AIR" : "FossID";

  // Add permission context
  const { canManageProjectWorkflow } = useContext(PermissionContext);
  const canManageWorkflow = canManageProjectWorkflow(projectManagerId);

  const fossIdRequestHooks = useFossIdVerificationRequest();
  const clarityAirRequestHooks = useClarityAirVerificationRequest();

  const {
    handleSubmitVerificationRequest,
    handleReUploadVerification,
    isDuplicateCheckPending,
    isCreateVerificationPending,
  } = isClarityAirTool ? clarityAirRequestHooks : fossIdRequestHooks;

  // Use the upload file with git hook for repository cloning
  const { cloneRepositoryViaPta, cloneRepositoryViaSsh } = useUploadFileWithGit();

  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: "",
    description: "",
    confirmText: "",
    confirmButtonVariant: "primary",
    onConfirm: null,
  });

  const [isCloneRepositoryPending, setIsCloneRepositoryPending] = useState(false);
  const [isReUploadVerificationPending, setIsReUploadVerificationPending] = useState(false);

  // State for re-verification modal with datetime picker
  const [reVerificationModal, setReVerificationModal] = useState({
    isOpen: false,
    selectedDateTime: new Date(),
  });

  // State for FossId verification request modal
  const [fossIdModal, setFossIdModal] = useState({
    isOpen: false,
    hasErrors: false,
    requestData: {},
    repoInfo: {},
  });

  // Add state for duplicate check modal
  const [duplicateModal, setDuplicateModal] = useState({
    isOpen: false,
    pendingUpload: null,
    pendingReuseCallback: null,
    pendingNewScanCallback: null,
  });

  // Add state for processing/loading
  const [processingRowId, setProcessingRowId] = useState(null);

  /* Loading state for workflow buttons */

  // Analysis Check
  const [isMoveToLoadIdentificationsLoading, setIsMoveToLoadIdentificationsLoading] = useState(false);
  // Load Identifications
  const [isMoveToUsageTypeCheckLoading, setIsMoveToUsageTypeCheckLoading] = useState(false);
  const [isRejectVerificationLoading, setIsRejectVerificationLoading] = useState(false);
  // Usage type check
  const [isRejectUsageTypeCheckLoading, setIsRejectUsageTypeCheckLoading] = useState(false);
  const [isMoveToUsageTypeInputLoading, setIsMoveToUsageTypeInputLoading] = useState(false);
  // Usage type input
  const [isRejectUsageTypeInputLoading, setIsRejectUsageTypeInputLoading] = useState(false);
  const [isMoveToReviewLoading, setIsMoveToReviewLoading] = useState(false);
  // Review
  const [isRejectReviewLoading, setIsRejectReviewLoading] = useState(false);
  const [isRequestReVerificationLoading, setIsRequestReVerificationLoading] = useState(false);
  const [isMoveToFollowUpLoading, setIsMoveToFollowUpLoading] = useState(false);
  const [isMoveToDoneLoading, setIsMoveToDoneLoading] = useState(false);
  // Follow-up
  const [isRejectFollowUpLoading, setIsRejectFollowUpLoading] = useState(false);
  const [isCompleteFollowUpLoading, setIsCompleteFollowUpLoading] = useState(false);
  // Check follow-up
  const [isRejectCheckFollowUpLoading, setIsRejectCheckFollowUpLoading] = useState(false);
  const [isCompleteCheckFollowUpLoading, setIsCompleteCheckFollowUpLoading] = useState(false);
  // Ready
  const [isDeCompleteVerificationLoading, setIsDeCompleteVerificationLoading] = useState(false);


  const [scannerSite, setScannerSite] = useState(() => (isClarityAirTool ? "" : SCAN_SITE_LINK));

  const formatModalTitle = (title) => (typeof title === "string" ? title.toUpperCase() : title);

  const showConfirmation = (title, description, confirmText, confirmButtonVariant, onConfirm) => {
    setConfirmationModal({
      isOpen: true,
      title: formatModalTitle(title),
      description,
      confirmText,
      confirmButtonVariant,
      onConfirm,
    });
  };

  const closeConfirmation = () => {
    setConfirmationModal({
      isOpen: false,
      title: "",
      description: "",
      confirmText: "",
      confirmButtonVariant: "primary",
      onConfirm: null,
    });
  };

  const handleConfirmAction = () => {
    if (confirmationModal.onConfirm) {
      confirmationModal.onConfirm();
    }
    closeConfirmation();
  };
  const getStepStatus = (stepIndex) => {
    if (stepIndex < currentStep) return "completed";
    if (stepIndex === currentStep) return "current";
    return "pending";
  };

  const getConnectorClasses = (stepIndex) => {
    const status = getStepStatus(stepIndex);
    const baseClasses = "h-1 flex-1 transition-all duration-200";
    if (status === "completed") {
      return `${baseClasses} ${colors.bgStatusSuccess}`;
    }
    return `${baseClasses} ${colors.bgStatusMuted}`;
  };

  // Function to check if step should display CheckedIcon
  const shouldShowCheckedIcon = (stepIndex, step) => {
    // Don't show any icon for step 0 (Request ReVerification)
    if (stepIndex === 0) {
      return false;
    }

    const status = getStepStatus(stepIndex);
    // Display CheckedIcon for completed steps OR Ready step when it's the current step
    return status === "completed" || (status === "current" && step.label === "Ready");
  };

  const handleOpenScanSite = () => {
    window.open(scannerSite, "_blank");
  };

  // Handle decomplete verification
  const handleDecompleteVerif = async () => {
    if (onDecompleteFromDone) {
      await onDecompleteFromDone();
    }
  };

  // Handle reject request verification
  const handleRejectReqVerif = async () => {
    // Determine preStatus based on current step
    let preStatus;
    if (currentStep === 3) {
      preStatus = PROGRESS_FOSSID_STATUS.REQUEST;
    } else if (currentStep === 6) {
      preStatus = PROGRESS_FOSSID_STATUS.REVIEW;
    } else if (currentStep === 7) {
      preStatus = PROGRESS_FOSSID_STATUS.FULFILLMENT;
    } else if (currentStep === 8) {
      preStatus = PROGRESS_FOSSID_STATUS.CHECK_FULFILLMENT;
    }
    // Call the reject function
    await onRejectVerification(currentStep, preStatus);
  };

  // Re-verification handlers
  const handleRequestReVerificationClick = () => {
    // Check if we're in re-verification stage (step 0)
    if (currentStep === 0) {
      // For re-verification stage, show a simple confirmation without datetime picker
      showConfirmation(
        t("common.re-verification-title"),
        t("common.re-verification-confirm"),
        t("common.re-verification-title"),
        "warning",
        () => onRequestReVerification(null),
      );
    } else {
      // For other stages, show modal with datetime picker
      setReVerificationModal({
        isOpen: true,
        selectedDateTime: new Date(),
      });
    }
  };

  const closeReVerificationModal = () => {
    setReVerificationModal({
      isOpen: false,
      selectedDateTime: new Date(),
    });
  };

  const handleFossIdModalOpen = () => {
    if (processingRowId !== null) return; // Prevent opening modal while processing
    setFossIdModal({
      isOpen: true,
      hasErrors: false,
      requestData: {},
      repoInfo: {},
    });
  };

  const handleFossIdModalClose = () => {
    if (processingRowId !== null) return; // Prevent closing modal while processing
    setFossIdModal({
      isOpen: false,
      hasErrors: false,
      requestData: {},
      repoInfo: {},
    });
  };

  const handleFossIdModalSubmit = async (data) => {
    const { accessToken, importMethod, repositoryType, message, scanTypeCd, sshUrl, url, verifFile } = data;

    // Handle repository cloning if needed
    if (importMethod === "repository") {
      try {
        if (accessToken) {
          const repoInfo = {
            url,
            token: accessToken,
          };
          setIsCloneRepositoryPending(true);
          await cloneRepositoryViaPta({ repoInfo, requestData: data });
          setIsCloneRepositoryPending(false);
        } else {
          const repoInfo = {
            url: sshUrl,
          };
          setIsCloneRepositoryPending(true);
          await cloneRepositoryViaSsh({ repoInfo, requestData: data });
          setIsCloneRepositoryPending(false);
        }
      } catch (error) {
        setIsCloneRepositoryPending(false);
        return; // Exit early if repository cloning fails
      }
    }

    // Handle duplicate check and processing
    const onDuplicateFound = (onReuse, onNewScan) => {
      // Support both old single callback and new dual callback patterns
      if (onNewScan) {
        // New pattern: two callbacks
        setDuplicateModal({
          isOpen: true,
          pendingUpload: null,
          pendingReuseCallback: onReuse,
          pendingNewScanCallback: onNewScan,
        });
      } else {
        // Legacy pattern: single callback (for backward compatibility)
        setDuplicateModal({
          isOpen: true,
          pendingUpload: onReuse,
          pendingReuseCallback: null,
          pendingNewScanCallback: null,
        });
      }
    };

    try {
      // Check if this is a re-upload (we have current verification data)
      const hasVerificationSeq = currentVerificationData && currentVerificationData.sourceCodeVerificationSeq;
      const hasClarityAirVerificationSeq = currentVerificationData && currentVerificationData.clarityAirVerificationSeq

      if (hasVerificationSeq || hasClarityAirVerificationSeq) {
        // Re-upload with PUT method
        const reUploadData = {
          sourceCodeVerificationSeq: currentVerificationData.sourceCodeVerificationSeq,
          requestComment: message || "",
          projectSeq: currentVerificationData.projectSeq || projectSeq,
          projectId: data.projectId || currentVerificationData.projectId || "unknown",
          verificationVersion: currentVerificationData.verificationVersion || 1,
          verifFile: data.verifFile,
          repositoryImportFlag: data.repositoryImportFlag || "N",
          ...(hasClarityAirVerificationSeq && {
            clarityAirVerificationSeq: currentVerificationData.clarityAirVerificationSeq,
            projectName: currentVerificationData.projectName || "unknown",
          }),
        };

        setIsReUploadVerificationPending(true);
        await handleReUploadVerification(
          reUploadData,
          onDuplicateFound,
          () => {
            navigate(`/projects/${projectSeq}`, {
              state: {
                activeTab: activeTabForNav,
                verificationStatus: "ANALYZING",
                refreshData: true,
              },
            });
          },
          setProcessingRowId,
        );
      } else {
        // New upload with POST method
        setIsReUploadVerificationPending(true);
        await handleSubmitVerificationRequest(
          {
            projectSeq,
            projectId: data.projectId || "unknown",
            projectName: data.projectName || "unknown",
            requestComment: message,
            ...data,
          },
          setProcessingRowId,
          onDuplicateFound,
        );
        setIsReUploadVerificationPending(false);
      }

      handleFossIdModalClose();
    } catch (error) {
      // Handle error silently
    } finally {
      setIsReUploadVerificationPending(false);
    }
  };

  // Handle duplicate modal close
  const handleDuplicateModalClose = () => {
    setDuplicateModal({
      isOpen: false,
      pendingUpload: null,
      pendingReuseCallback: null,
      pendingNewScanCallback: null,
    });
  };

  // Handle duplicate confirm (legacy)
  const handleDuplicateConfirm = () => {
    if (duplicateModal.pendingUpload) {
      duplicateModal.pendingUpload();
    }
    handleDuplicateModalClose();
    // Navigate to project detail page after duplicate confirmation
    navigate(`/projects/${projectSeq}`, {
      state: {
        activeTab: activeTabForNav,
        verificationStatus: "ANALYZING",
        refreshData: true,
      },
    });
  };

  const handleReuseScan = () => {
    if (duplicateModal.pendingReuseCallback) {
      duplicateModal.pendingReuseCallback();
    }
    handleDuplicateModalClose();
    // Navigate to project detail page after reuse confirmation
    navigate(`/projects/${projectSeq}`, {
      state: {
        activeTab: activeTabForNav,
        refreshData: true,
      },
    });
  };

  const handleNewScan = () => {
    if (duplicateModal.pendingNewScanCallback) {
      duplicateModal.pendingNewScanCallback();
    }
    handleDuplicateModalClose();
    // Navigate to project detail page after new scan confirmation
    navigate(`/projects/${projectSeq}`, {
      state: {
        activeTab: activeTabForNav,
        refreshData: true,
      },
    });
  };

  const handleSetHasErrors = useCallback((hasErrors) => {
    setFossIdModal((prev) => ({ ...prev, hasErrors }));
  }, []);

  const handleSetRequestData = useCallback((requestData) => {
    setFossIdModal((prev) => ({ ...prev, requestData }));
  }, []);

  const handleSetRepoInfo = useCallback((repoInfo) => {
    setFossIdModal((prev) => ({ ...prev, repoInfo }));
  }, []);

  const handleReVerificationConfirm = async () => {
    if (isRequestReVerificationLoading) return;

    setIsRequestReVerificationLoading(true);
    try {
      if (onRequestReVerification) {
        await onRequestReVerification(reVerificationModal.selectedDateTime);
      }
    } catch (error) {
      console.error("Error in onRequestReVerification:", error);
    } finally {
      setIsRequestReVerificationLoading(false);
    }
    closeReVerificationModal();
  };

  // Handle de-complete verification
  const handleDeCompleteVerification = async () => {
    if (onToFollowUpResult) {
      await onToFollowUpResult();
    }
    if (onCompleteVerification) {
      await onCompleteVerification();
    }
  };

  // Handle datetime change in re-verification modal
  const handleDateTimeChange = (newDateTime) => {
    setReVerificationModal((prev) => ({
      ...prev,
      selectedDateTime: newDateTime,
    }));
  };

  const clientConfigContext = useClientConfig();

  useEffect(() => {
    if (steps[currentStep]?.label !== "Load Identifications") {
      return;
    }
    const getClientConfigValue = clientConfigContext?.getClientConfigValue || (() => null);
    const baseScannerSiteUrl = isClarityAirTool
      ? getClientConfigValue("clarityAirSiteUrl")
      : getClientConfigValue("fossIdSiteUrl");

    if (!baseScannerSiteUrl) {
      return;
    }

    const normalizedBaseUrl = baseScannerSiteUrl.replace(/\/$/, "");
    let scannerSiteUrl = baseScannerSiteUrl;

    if (isClarityAirTool) {
      const clarityAirScanId = currentVerificationData?.clarityAirScanId;
      if (clarityAirScanId) {
        scannerSiteUrl = `${normalizedBaseUrl}/#/folder/${clarityAirScanId}`;
      }
    } else {
      const fossidScanCode = currentVerificationData?.fossidScanCode;
      if (fossidScanCode) {
        scannerSiteUrl = `${normalizedBaseUrl}/nui/scans/active?search=${encodeURIComponent(fossidScanCode)}`;
      }
    }

    setScannerSite(scannerSiteUrl);
  }, [
    currentStep,
    steps,
    isClarityAirTool,
    currentVerificationData?.clarityAirScanId,
    currentVerificationData?.fossidScanCode,
    clientConfigContext?.clientConfig,
  ]);

  // Handle ESC key for re-verification modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape" && reVerificationModal.isOpen) {
        closeReVerificationModal();
      }
    };

    if (reVerificationModal.isOpen) {
      document.addEventListener("keydown", handleEscKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey);
      document.body.style.overflow = "unset";
    };
  }, [reVerificationModal.isOpen]);

  // Function to render buttons based on current step
  const renderStepButtons = () => {
    const currentStepData = steps[currentStep];
    if (!currentStepData) return null;

    const stepLabel = currentStepData.label;
    const stepSlug = workflowStepSlug(stepLabel);
    const stid = (name) => tid(`step-${stepSlug}-${name}`);

    switch (stepLabel) {
      case "Request ReVerification":
        return (
          <div className="flex justify-end items-center mt-4" data-testid={stid("actions")}>
            <div className="flex gap-2">
              <PrimaryButton
                data-testid={stid("re-upload-button")}
                size="lg"
                onClick={handleFossIdModalOpen}
                disabled={processingRowId !== null}
              >
                {t("common.re-upload-button")}
              </PrimaryButton>
            </div>
          </div>
        );

      case "Analyzing":
        return null;

      case "Analysis Check":
        return (
          <div className="flex justify-end items-center mt-4" data-testid={stid("actions")}>
            <div className="flex gap-2">
              <button
                data-testid={stid("request-button")}
                disabled={isMoveToLoadIdentificationsLoading}
                onClick={() => {
                  showConfirmation(
                    t("fossid-status-confirm-modal.load-identifications"),
                    t("fossid-status-confirm-modal.are-you-sure-load-identifications"),
                    t("common.ok-button"),
                    "primary",
                    async () => {
                      setIsMoveToLoadIdentificationsLoading(true);
                      try {
                        if (onMoveToLoadIdentifications) {
                          await onMoveToLoadIdentifications();
                        }
                      } catch (error) {
                        console.error("Error in onMoveToLoadIdentifications:", error);
                      } finally {
                        setIsMoveToLoadIdentificationsLoading(false);
                      }
                    },
                  );
                }}
                className={`px-4 py-2 font-600 inline-flex items-center justify-center rounded-lg text-sm font-semibold shadow-sm ${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} whitespace-nowrap transition-colors ${isMoveToLoadIdentificationsLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {isMoveToLoadIdentificationsLoading && <Spin className="mr-2" />}
                {t("fossid-status-options.request")}
              </button>
            </div>
          </div>
        );

      case "Load Identifications":
        return (
          <div className="flex justify-between items-center mt-4" data-testid={stid("actions")}>
            <div className="flex gap-2">
              {canManageWorkflow && (
                <button
                  data-testid={stid("reject-verification-button")}
                  disabled={isRejectVerificationLoading || isMoveToUsageTypeCheckLoading}
                  onClick={() => {
                    showConfirmation(
                      t("fossid-status-confirm-modal.reject-verification"),
                      t("fossid-status-confirm-modal.are-you-sure-reject"),
                      t("common.workflow.reject"),
                      "danger",
                      async () => {
                        setIsRejectVerificationLoading(true);
                        try {
                          await handleRejectReqVerif();
                        } catch (error) {
                          console.error("Error in handleRejectReqVerif:", error);
                        } finally {
                          setIsRejectVerificationLoading(false);
                        }
                      },
                    );
                  }}
                  className={`min-w-28 min-h-10 px-4 py-2 ${colors.bgButtonDanger} ${colors.textPrimary} text-sm font-medium transition-colors rounded-lg hover:opacity-80 ${isRejectVerificationLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {isRejectVerificationLoading && <Spin className="mr-2" />}
                  {t("communication.reject")}
                </button>
              )}
            </div>
            <div className="flex gap-2">
              {canManageWorkflow && (
                <>
                  <button
                    data-testid={stid("open-scanner-site-button")}
                    onClick={() =>
                      showConfirmation(
                        t("fossid-status-confirm-modal.open-scanner-site", { scannerSiteName }),
                        t("fossid-status-confirm-modal.are-you-sure-open-scanner-site"),
                        t("common.ok-button"),
                        "primary",
                        handleOpenScanSite,
                      )
                    }
                    className={`min-w-28 min-h-10 border rounded-lg px-4 py-2 text-sm font-medium transition-colors ${colors.borderActiveDashed} ${colors.textActive} ${colors.bgButton} hover:opacity-80`}
                  >
                    {t("fossid-status-options-button.open-scanner-site", { scannerSiteName })}
                  </button>
                  <button
                    data-testid={stid("load-identification-result-button")}
                    disabled={isMoveToUsageTypeCheckLoading || isRejectVerificationLoading}
                    onClick={() => {
                      showConfirmation(
                        t("fossid-status-confirm-modal.request-usage-type-check"),
                        t("fossid-status-confirm-modal.are-you-sure-request-usage-type-check"),
                        t("common.confirm"),
                        "primary",
                        async () => {
                          setIsMoveToUsageTypeCheckLoading(true);
                          try {
                            if (onMoveToUsageTypeCheck) {
                              await onMoveToUsageTypeCheck("Y");
                            }
                          } catch (error) {
                            console.error("Error in onMoveToUsageTypeCheck:", error);
                          } finally {
                            setIsMoveToUsageTypeCheckLoading(false);
                          }
                        },
                      );
                    }}
                    className={`min-w-28 min-h-10 px-4 py-2 font-600 inline-flex items-center justify-center rounded-lg text-sm font-semibold shadow-sm ${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} whitespace-nowrap transition-colors ${isMoveToUsageTypeCheckLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {isMoveToUsageTypeCheckLoading && <Spin className="mr-2" />}
                    {t("fossid-status-options-button.load-identification-result")}
                  </button>
                </>
              )}
            </div>
          </div>
        );

      case "Usage type check": // Current step: Usage type review
        return (
          <div className="flex justify-between items-center mt-4" data-testid={stid("actions")}>
            <div className="flex gap-2">
              {canManageWorkflow && (
                <button
                  data-testid={stid("reject-button")}
                  disabled={isRejectUsageTypeCheckLoading || isMoveToUsageTypeInputLoading}
                  className={`min-w-28 min-h-10 px-4 py-2 ${colors.bgButtonDanger} ${colors.textPrimary} text-sm font-medium transition-colors rounded-lg hover:opacity-80 ${isRejectUsageTypeCheckLoading || isMoveToUsageTypeInputLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                  onClick={() => {
                    showConfirmation(
                      t("common.workflow.reject-usage-type-check"),
                      t("common.workflow.reject-usage-type-check-confirm"),
                      t("common.workflow.reject"),
                      "danger",
                      async () => {
                        setIsRejectUsageTypeCheckLoading(true);
                        try {
                          if (onRejectUsageTypeCheck) {
                            await onRejectUsageTypeCheck();
                          }
                        } catch (error) {
                          console.error("Error in onRejectUsageTypeCheck:", error);
                        } finally {
                          setIsRejectUsageTypeCheckLoading(false);
                        }
                      },
                    );
                  }}
                >
                  {isRejectUsageTypeCheckLoading && <Spin className="mr-2" />}
                  {t("common.workflow.reject")}
                </button>
              )}
            </div>
            {canManageWorkflow && (
              <div className="flex gap-2">
                <PrimaryButton
                  data-testid={stid("usage-type-input-request-button")}
                  size="lg"
                  disabled={isRejectUsageTypeCheckLoading || isMoveToUsageTypeInputLoading}
                  onClick={() => {
                    showConfirmation(
                      t("fossid-status-confirm-modal.request-usage-type-input"),
                      t("fossid-status-confirm-modal.are-you-sure-request-input"),
                      t("common.confirm"),
                      "primary",
                      async () => {
                        setIsMoveToUsageTypeInputLoading(true);
                        try {
                          if (onMoveToUsageTypeInput) {
                            await onMoveToUsageTypeInput("Y");
                          }
                        } catch (error) {
                          console.error("Error in onMoveToUsageTypeInput:", error);
                        } finally {
                          setIsMoveToUsageTypeInputLoading(false);
                        }
                      },
                    );
                  }}
                >
                  {isMoveToUsageTypeInputLoading && <Spin className="mr-2" />}
                  {t("fossid-status-options-button.usage-type-input-req")}
                </PrimaryButton>
              </div>
            )}
          </div>
        );

      case "Usage type input":
        return (
          <div className="flex justify-between items-center mt-4" data-testid={stid("actions")}>
            <div className="flex gap-2">
              {canManageWorkflow && (
                <button
                  data-testid={stid("reject-button")}
                  disabled={isRejectUsageTypeInputLoading || isMoveToReviewLoading}
                  className={`min-w-28 min-h-10 px-4 py-2 ${colors.bgButtonDanger} ${colors.textPrimary} text-sm font-medium transition-colors rounded-lg hover:opacity-80 ${isRejectUsageTypeInputLoading || isMoveToReviewLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                  onClick={() => {
                    showConfirmation(
                      t("common.workflow.reject-usage-type-input"),
                      t("common.workflow.reject-usage-type-input-confirm"),
                      t("common.workflow.reject"),
                      "danger",
                      async () => {
                        setIsRejectUsageTypeInputLoading(true);
                        try {
                          if (onRejectUsageTypeInput) {
                            await onRejectUsageTypeInput();
                          }
                        } catch (error) {
                          console.error("Error in onRejectUsageTypeInput:", error);
                        } finally {
                          setIsRejectUsageTypeInputLoading(false);
                        }
                      },
                    );
                  }}
                >
                  {isRejectUsageTypeInputLoading && <Spin className="mr-2" />}
                  {t("common.workflow.reject")}
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button
                data-testid={stid("request-review-button")}
                disabled={isRejectUsageTypeInputLoading || isMoveToReviewLoading}
                onClick={() => {
                  showConfirmation(
                    t("project.clarity.sub-verification-status.request-review"),
                    t("fossid-status-confirm-modal.are-you-sure-request-review"),
                    t("common.ok-button"),
                    "primary",
                    async () => {
                      setIsMoveToReviewLoading(true);
                      try {
                        if (onMoveToReview) {
                          await onMoveToReview();
                        }
                      } catch (error) {
                        console.error("Error in onMoveToReview:", error);
                      } finally {
                        setIsMoveToReviewLoading(false);
                      }
                    },
                  );
                }}
                className={`px-4 py-2 font-600 inline-flex items-center justify-center rounded-lg text-sm font-semibold shadow-sm ${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} whitespace-nowrap transition-colors ${isRejectUsageTypeInputLoading || isMoveToReviewLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {isMoveToReviewLoading && <Spin className="mr-2" />}
                {t("project.clarity.sub-verification-status.request-review")}
              </button>
            </div>
          </div>
        );

      case "Review":
        const isReviewActionLoading = isRejectReviewLoading || isRequestReVerificationLoading || isMoveToFollowUpLoading || isMoveToDoneLoading;
        return (
          <div className="flex justify-between items-center mt-4" data-testid={stid("actions")}>
            {canManageWorkflow && (
              <>
                <div className="flex gap-2">
                  <button
                    data-testid={stid("reject-button")}
                    disabled={isReviewActionLoading}
                    onClick={() => {
                      showConfirmation(
                        t("project.verification-report.confirm-reject-review-title"),
                        t("fossid-status-confirm-modal.are-you-sure-reject-review"),
                        t("common.workflow.reject"),
                        "danger",
                        async () => {
                          setIsRejectReviewLoading(true);
                          try {
                            await handleRejectReqVerif();
                          } catch (error) {
                            console.error("Error in handleRejectReqVerif:", error);
                          } finally {
                            setIsRejectReviewLoading(false);
                          }
                        },
                      );
                    }}
                    className={`min-w-28 min-h-10 px-4 py-2 ${colors.bgButtonDanger} ${colors.textPrimary} text-sm font-medium transition-colors rounded-lg hover:opacity-80 ${isReviewActionLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {isRejectReviewLoading && <Spin className="mr-2" />}
                    {t("communication.reject")}
                  </button>
                </div>
                <div className="flex gap-2">
                  <button
                    data-testid={stid("request-re-verification-button")}
                    disabled={isReviewActionLoading}
                    onClick={handleRequestReVerificationClick}
                    className={`min-w-28 min-h-10 rounded-lg border px-4 py-2 text-sm font-medium transition-colors inline-flex items-center justify-center ${colors.borderActiveDashed} ${colors.textActive} ${colors.bgButton} hover:opacity-80 ${isReviewActionLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {isRequestReVerificationLoading && <Spin className="mr-2" />}
                    {t("common.Request-Re-Verif")}
                  </button>
                  <button
                    data-testid={stid("order-follow-up-button")}
                    disabled={isReviewActionLoading}
                    onClick={() => {
                      showConfirmation(
                        t("fossid-status-confirm-modal.order-follow-up"),
                        t("fossid-status-confirm-modal.are-you-sure-order-follow-up"),
                        t("common.confirm"),
                        "primary",
                        async () => {
                          setIsMoveToFollowUpLoading(true);
                          try {
                            if (onMoveToFollowUp) {
                              await onMoveToFollowUp();
                            }
                          } catch (error) {
                            console.error("Error in onMoveToFollowUp:", error);
                          } finally {
                            setIsMoveToFollowUpLoading(false);
                          }
                        },
                      );
                    }}
                    className={`min-w-28 min-h-10 px-4 py-2 font-600 inline-flex items-center justify-center rounded-lg text-sm font-semibold shadow-sm ${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} whitespace-nowrap transition-colors ${isReviewActionLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {isMoveToFollowUpLoading && <Spin className="mr-2" />}
                    {t("fossid-status-options-button.order-follow-up")}
                  </button>
                  <button
                    data-testid={stid("force-complete-verification-button")}
                    disabled={isReviewActionLoading}
                    onClick={() => {
                      showConfirmation(
                        t("common.workflow.force-complete-verification"),
                        t("fossid-status-confirm-modal.are-you-sure-force-complete"),
                        t("common.confirm"),
                        "primary",
                        async () => {
                          setIsMoveToDoneLoading(true);
                          try {
                            if (onMoveToDone) {
                              await onMoveToDone();
                            }
                          } catch (error) {
                            console.error("Error in onMoveToDone:", error);
                          } finally {
                            setIsMoveToDoneLoading(false);
                          }
                        },
                      );
                    }}
                    className={`min-w-28 min-h-10 px-4 py-2 font-600 inline-flex items-center justify-center rounded-lg text-sm font-semibold shadow-sm ${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} whitespace-nowrap transition-colors ${isReviewActionLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {isMoveToDoneLoading && <Spin className="mr-2" />}
                    {t("fossid-status-options-button.force-complete-verification")}
                  </button>
                </div>
              </>
            )}
          </div>
        );

      case "Follow-up":
        const isFollowUpActionLoading = isRejectFollowUpLoading || isRequestReVerificationLoading || isCompleteFollowUpLoading || isMoveToDoneLoading;
        return (
          <div className="flex justify-between items-center mt-4" data-testid={stid("actions")}>
            <div className="flex gap-2">
              {canManageWorkflow && (
                <button
                  data-testid={stid("reject-button")}
                  disabled={isFollowUpActionLoading}
                  onClick={() => {
                    showConfirmation(
                      t("fossid-status-confirm-modal.reject-follow-up"),
                      t("fossid-status-confirm-modal.are-you-sure-reject-follow-up"),
                      t("common.workflow.reject"),
                      "danger",
                      async () => {
                        setIsRejectFollowUpLoading(true);
                        try {
                          await handleRejectReqVerif();
                        } catch (error) {
                          console.error("Error in handleRejectReqVerif:", error);
                        } finally {
                          setIsRejectFollowUpLoading(false);
                        }
                      },
                    );
                  }}
                  className={`min-w-28 min-h-10 px-4 py-2 ${colors.bgButtonDanger} ${colors.textPrimary} text-sm font-medium transition-colors rounded-lg hover:opacity-80 ${isFollowUpActionLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {isRejectFollowUpLoading && <Spin className="mr-2" />}
                  {t("communication.reject")}
                </button>
              )}
            </div>
            <div className="flex gap-2">
              {canManageWorkflow && (
                <button
                  data-testid={stid("request-re-verification-button")}
                  disabled={isFollowUpActionLoading}
                  onClick={handleRequestReVerificationClick}
                  className={`min-w-28 min-h-10 rounded-lg border px-4 py-2 text-sm font-medium transition-colors inline-flex items-center justify-center ${colors.borderActiveDashed} ${colors.textActive} ${colors.bgButton} hover:opacity-80 ${isFollowUpActionLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {isRequestReVerificationLoading && <Spin className="mr-2" />}
                  {t("common.Request-Re-Verif")}
                </button>
              )}
              <button
                data-testid={stid("complete-follow-up-button")}
                disabled={isFollowUpActionLoading}
                onClick={() => {
                  showConfirmation(
                    t("fossid-status-confirm-modal.complete-follow-up"),
                    t("fossid-status-confirm-modal.are-you-sure-complete-follow-up"),
                    t("common.confirm"),
                    "primary",
                    async () => {
                      setIsCompleteFollowUpLoading(true);
                      try {
                        if (onMoveToFollowCheckUp) {
                          await onMoveToFollowCheckUp();
                        }
                      } catch (error) {
                        console.error("Error in onMoveToFollowCheckUp:", error);
                      } finally {
                        setIsCompleteFollowUpLoading(false);
                      }
                    },
                  );
                }}
                className={`min-w-28 min-h-10 px-4 py-2 font-600 inline-flex items-center justify-center rounded-lg text-sm font-semibold shadow-sm ${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} whitespace-nowrap transition-colors ${isFollowUpActionLoading ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {isCompleteFollowUpLoading && <Spin className="mr-2" />}
                {t("fossid-status-options-button.complete-follow-up")}
              </button>
              {canManageWorkflow && (
                <button
                  data-testid={stid("force-complete-verification-button")}
                  disabled={isFollowUpActionLoading}
                  onClick={() => {
                    showConfirmation(
                      t("common.workflow.force-complete-verification"),
                      t("fossid-status-confirm-modal.are-you-sure-force-complete"),
                      t("common.confirm"),
                      "primary",
                      async () => {
                        setIsMoveToDoneLoading(true);
                        try {
                          if (onMoveToDone) {
                            await onMoveToDone();
                          }
                        } catch (error) {
                          console.error("Error in onMoveToDone:", error);
                        } finally {
                          setIsMoveToDoneLoading(false);
                        }
                      },
                    );
                  }}
                  className={`min-w-28 min-h-10 px-4 py-2 font-600 inline-flex items-center justify-center rounded-lg text-sm font-semibold shadow-sm ${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} whitespace-nowrap transition-colors ${isFollowUpActionLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {isMoveToDoneLoading && <Spin className="mr-2" />}
                  {t("fossid-status-options-button.force-complete-verif")}
                </button>
              )}
            </div>
          </div>
        );
      case "Check follow-up":
        const isCheckFollowUpActionLoading = isRejectCheckFollowUpLoading || isRequestReVerificationLoading || isCompleteCheckFollowUpLoading;
        return (
          <div className="flex justify-between items-center mt-4" data-testid={stid("actions")}>
            {canManageWorkflow && (
              <>
                <div className="flex gap-2">
                  <button
                    data-testid={stid("reject-button")}
                    disabled={isCheckFollowUpActionLoading}
                    onClick={() => {
                      showConfirmation(
                        t("fossid-status-confirm-modal.reject-check-follow-up"),
                        t("fossid-status-confirm-modal.are-you-sure-reject-check-follow-up"),
                        t("common.workflow.reject"),
                        "danger",
                        async () => {
                          setIsRejectCheckFollowUpLoading(true);
                          try {
                            await handleRejectReqVerif();
                          } catch (error) {
                            console.error("Error in handleRejectReqVerif:", error);
                          } finally {
                            setIsRejectCheckFollowUpLoading(false);
                          }
                        },
                      );
                    }}
                    className={`min-w-28 min-h-10 px-4 py-2 ${colors.bgButtonDanger} ${colors.textPrimary} text-sm font-medium transition-colors rounded-lg hover:opacity-80 ${isCheckFollowUpActionLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {isRejectCheckFollowUpLoading && <Spin className="mr-2" />}
                    {t("communication.reject")}
                  </button>
                </div>
                <div className="flex gap-2">
                  <button
                    data-testid={stid("request-re-verification-button")}
                    disabled={isCheckFollowUpActionLoading}
                    onClick={handleRequestReVerificationClick}
                    className={`min-w-28 min-h-10 rounded-lg border px-4 py-2 text-sm font-medium transition-colors inline-flex items-center justify-center ${colors.borderActiveDashed} ${colors.textActive} ${colors.bgButton} hover:opacity-80 ${isCheckFollowUpActionLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {isRequestReVerificationLoading && <Spin className="mr-2" />}
                    {t("common.Request-Re-Verif")}
                  </button>
                  <button
                    data-testid={stid("complete-verification-button")}
                    disabled={isCheckFollowUpActionLoading}
                    onClick={() => {
                      showConfirmation(
                        t("common.complete-verification"),
                        t("fossid-status-confirm-modal.complete-verification-confirm"),
                        t("common.confirm"),
                        "primary",
                        async () => {
                          setIsCompleteCheckFollowUpLoading(true);
                          try {
                            await handleDeCompleteVerification();
                          } catch (error) {
                            console.error("Error in handleDeCompleteVerification:", error);
                          } finally {
                            setIsCompleteCheckFollowUpLoading(false);
                          }
                        },
                      );
                    }}
                    className={`min-w-28 min-h-10 px-4 py-2 font-600 inline-flex items-center justify-center rounded-lg text-sm font-semibold shadow-sm ${colors.buttonBackgroundGradient} ${colors.buttonBackgroundGradientHover} whitespace-nowrap transition-colors ${isCheckFollowUpActionLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    {isCompleteCheckFollowUpLoading && <Spin className="mr-2" />}
                    {forceCompleteText
                      ? t("common.workflow.force-complete-verification")
                      : t("common.complete-verification")}
                  </button>
                </div>
              </>
            )}
          </div>
        );

      case "Ready":
        return (
          <div className="flex justify-between items-center mt-4" data-testid={stid("actions")}>
            <div className="flex gap-2">
              {canManageWorkflow && (
                <button
                  data-testid={stid("decomplete-verification-button")}
                  disabled={isDeCompleteVerificationLoading}
                  onClick={() => {
                    showConfirmation(
                      t("fossid-status-confirm-modal.decomplete-verification"),
                      t("fossid-status-confirm-modal.are-you-sure-decomplete"),
                      t("fossid-status-confirm-modal.decomplete-verification"),
                      "danger",
                      async () => {
                        setIsDeCompleteVerificationLoading(true);
                        try {
                          await handleDecompleteVerif();
                        } catch (error) {
                          console.error("Error in handleDecompleteVerif:", error);
                        } finally {
                          setIsDeCompleteVerificationLoading(false);
                        }
                      },
                    );
                  }}
                  className={`min-w-28 min-h-10 px-4 py-2 ${colors.bgButtonDanger} ${colors.textPrimary} text-sm font-medium transition-colors rounded-lg hover:opacity-80 ${isDeCompleteVerificationLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  {isDeCompleteVerificationLoading && <Spin className="mr-2" />}
                  {t("fossid-status-options-button.decomplete-verif")}
                </button>
              )}
            </div>
          </div>
        );

      default:
        return (
          <div className="flex justify-between items-center mt-4">
            <div className="flex gap-2"></div>
          </div>
        );
    }
  };

  const isAnyLoading = isLoading || isFetching;
  const [isSubmitting, setIsSubmitting] = useState(false);

  return (
    <div className={`w-full h-40 relative ${className}`} data-testid={tid("root")}>
      {isAnyLoading && (
        <div
          data-testid={tid("loading-overlay")}
          className={`absolute inset-0 z-50 flex flex-col items-center justify-center ${colors.bgPrimary} rounded-lg`}
        >
          <DotLoader
            color={colors.textActiveColor}
            loading={isAnyLoading}
            size={60}
            speedMultiplier={1.2}
            aria-label="Loading"
          />
        </div>
      )}
      {currentStep === 0 ? (
        // Show special interface for step 0 (Request ReVerification)
        <>
          <div className="flex justify-center items-center px-8 mt-4 mb-12" data-testid={tid("re-verification-step")}>
            <div className="relative flex flex-col items-center">
              <div>
                <div className={`w-14 h-14 ${colors.textSecondary} flex items-center justify-center opacity-60`}>
                  <svg width="56" height="56" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {/* Git-like branching icon with three circles connected by lines */}
                    <circle cx="16" cy="16" r="5" fill="currentColor" />
                    <circle cx="16" cy="48" r="5" fill="currentColor" />
                    <circle cx="48" cy="48" r="5" fill="currentColor" />
                    <path
                      d="M16 21 L16 43 M16 43 L48 43"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              </div>
              <div
                className={`absolute top-16 left-1/2 -translate-x-1/2 -translate-y-1/2 mt-2 text-center text-sm font-semibold whitespace-nowrap px-3 py-1 border-2 border-dashed rounded-lg ${colors.borderActiveDashed} ${colors.textActive} bg-opacity-10 ${colors.bgButton}`}
              >
                {t("status.re-verification-request")}
              </div>
            </div>
          </div>
          {renderStepButtons()}
        </>
      ) : (
        // Show normal workflow steps for other steps
        <>
          <div className="flex justify-center items-center px-8 mt-4 mb-20" data-testid={tid("steps-track")}>
            {steps.slice(1).map((step, index) => (
              <React.Fragment key={step.label || index}>
                <div
                  className="relative flex flex-col items-center"
                  data-testid={tid(`step-${workflowStepSlug(step.label)}`)}
                >
                  <div>
                    {shouldShowCheckedIcon(index + 1, step) ? (
                      <CheckedIcon size={56} />
                    ) : (
                      step.icon && <step.icon size={56} isActive={getStepStatus(index + 1) === "current"} />
                    )}
                  </div>
                  <div
                    className={`absolute top-16 left-1/2 -translate-x-1/2 mt-2 min-w-30 max-[1600px]:max-w-[120px] text-center text-sm font-semibold whitespace-nowrap max-[1600px]:whitespace-normal max-[1600px]:break-words ${getStepStatus(index + 1) === "current" && step.label !== "Ready" ? colors.textStatusActiveStep : colors.textPrimary}`}
                  >
                    {step.displayLabel}
                  </div>
                </div>
                {index < steps.slice(1).length - 1 && (
                  <div className={`${getConnectorClasses(index + 1)} max-w-40`} key={`connector-${index}`} />
                )}
              </React.Fragment>
            ))}
          </div>
          {renderStepButtons()}
        </>
      )}
      {/* Confirmation Modal */}
      <ConfirmActionModal
        data-testid={tid("confirmation-modal")}
        isOpen={confirmationModal.isOpen}
        onClose={closeConfirmation}
        onConfirm={handleConfirmAction}
        title={confirmationModal.title}
        description={confirmationModal.description}
        confirmText={confirmationModal.confirmText}
        confirmButtonVariant={confirmationModal.confirmButtonVariant}
        cancelText={t("common.cancel-button")}
      />

      {/* Re-Verification Modal with DateTime Picker */}
      {reVerificationModal.isOpen && (
        <div
          data-testid={tid("re-verification-modal-overlay")}
          className={`fixed inset-0 z-[9998] flex items-center justify-start md:justify-center p-4 ${colors.bgBlurModal} backdrop-blur-[1px] overflow-x-auto`}
          onClick={closeReVerificationModal}
        >
          <div
            data-testid={tid("re-verification-modal")}
            className={`relative w-[448px] min-w-[448px] ${colors.bgModal} transform rounded-lg shadow-xl transition-all duration-200`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Content */}
            <div className="p-6">
              {/* Icon and Title */}
              <div className="mb-4 flex items-center justify-center">
                <div className="mx-auto flex h-16 w-16 items-center justify-center">
                  <CalendarIcon size={48} />
                </div>
              </div>

              {/* Title */}
              <div className="mb-4 text-center">
                <h3 className={`text-lg font-semibold ${colors.textPrimary} mb-2`}>
                  {t("common.re-verification-title")}
                </h3>

                {/* Description */}
                <p className={`text-sm ${colors.textSecondary} mb-4`}>{t("common.re-verification-with-date")}</p>

                {/* DateTime Picker */}
                <div className="mb-4">
                  <label className={`block text-sm font-medium ${colors.textPrimary} mb-2`}>
                    {t("common.due-date-time")}
                  </label>
                  <CustomDatePicker
                    data-testid={tid("re-verification-datetime-picker")}
                    selected={reVerificationModal.selectedDateTime}
                    onChange={handleDateTimeChange}
                    minDate={new Date()}
                    dateFormat="yyyy-MM-dd"
                    filterDate={(date) => {
                      const today = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());
                      return date >= today;
                    }}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex gap-3 flex-row justify-center">
                {/* Cancel Button */}
                <button
                  data-testid={tid("re-verification-cancel-button")}
                  onClick={closeReVerificationModal}
                  className={`rounded-md border px-4 py-2 text-sm font-medium transition-colors ${colors.borderPrimary} ${colors.textSecondary} ${colors.bgSecondary} min-w-[100px] ${colors.focusRing2} focus:outline-none`}
                >
                  {t("common.cancel-button")}
                </button>

                {/* Confirm Button */}
                <PrimaryButton
                  data-testid={tid("re-verification-confirm-button")}
                  size="lg"
                  onClick={handleReVerificationConfirm}
                  disabled={isRequestReVerificationLoading}
                >
                  {isRequestReVerificationLoading && <Spin className="mr-2" />}
                  {isRequestReVerificationLoading ? t("update_messages.PROCESSING") : t("common.confirm")}
                </PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Source verification request modal (FossID or ClarityAIR) */}
      {isClarityAirTool ? (
        <ClarityAirVerificationRequest
          isOpen={fossIdModal.isOpen}
          onClose={handleFossIdModalClose}
          onSubmit={handleFossIdModalSubmit}
          setHasErrors={handleSetHasErrors}
          setRequestData={handleSetRequestData}
          setRepoInfo={handleSetRepoInfo}
          isLoading={processingRowId !== null}
        />
      ) : (
        <FossIdVerificationRequest
          isOpen={fossIdModal.isOpen}
          onClose={handleFossIdModalClose}
          onSubmit={handleFossIdModalSubmit}
          setHasErrors={handleSetHasErrors}
          setRequestData={handleSetRequestData}
          setRepoInfo={handleSetRepoInfo}
          isLoading={processingRowId !== null}
        />
      )}

      {/* Duplicate Modal */}
      {duplicateModal.isOpen && (
        <ConfirmProcessingModal
          data-testid={tid("duplicate-file-modal")}
          isOpen={duplicateModal.isOpen}
          onClose={handleDuplicateModalClose}
          onConfirm={handleDuplicateConfirm}
          onReuseScan={duplicateModal.pendingReuseCallback ? handleReuseScan : undefined}
          onNewScan={duplicateModal.pendingNewScanCallback ? handleNewScan : undefined}
          title={formatModalTitle(t("project.duplicate-file-warning"))}
          message={duplicateModal.pendingReuseCallback && duplicateModal.pendingNewScanCallback ? t("project.duplicate-file-warning-message-new") : t("project.duplicate-file-warning-description")}
          confirmText={t("common.yes")}
          cancelText={t("common.cancel-button")}
          reuseText={t("project.reuse-previous-scan-results")}
          newScanText={t("project.perform-new-scan")}
        />
      )}
      <FullScreenDotLoader
        loading={
          isDuplicateCheckPending ||
          isCreateVerificationPending ||
          isCloneRepositoryPending ||
          isReUploadVerificationPending ||
          processingRowId !== null
        }
        text={
          isDuplicateCheckPending
            ? t("project.verification-request.validating-file") || "Validating file..."
            : isCloneRepositoryPending
              ? t("project.verification-request.cloning-repository") || "Cloning repository..."
              : isReUploadVerificationPending || isCreateVerificationPending
                ? t("project.verification-request.upload-verification-file") || "Uploading verification file..."
                : t("project.verification-request.upload-verification-file") || "Uploading verification file..."
        }
        size={80}
        speedMultiplier={1.2}
      />
    </div>
  );
};

export default WorkflowProgressFossID;
